import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Ανάγνωση του γραφήματος Ενεργειακού Ισοζυγίου',
    summary: 'Πώς να διαβάσετε το ωριαίο γράφημα που εμφανίζει κάθε πηγή και χρήση ενέργειας μετά από μια βελτιστοποίηση.',
    body: (
      <>
        <p>
          Το γράφημα <strong>Ενεργειακό Ισοζύγιο</strong> εμφανίζεται κάτω από τη Σύνοψη KPI στα
          αποτελέσματα Βελτιστοποίησης (Modelling). Είναι ένα ωριαίο γράφημα εμβαδού με μία σειρά
          για κάθε τύπο πηγής ή χρήσης, με δυνατότητα εναλλαγής μεταξύ κοινοτικής και ανά κτίριο
          προβολής.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Ενεργειακό Ισοζύγιο κατά την βελτιστοποιημένη ημέρα."
        />

        <h5 className="mt-3">Οι σειρές που θα δείτε</h5>
        <p>Το γράφημα εμφανίζει έως τις παρακάτω σειρές — εμφανίζονται μόνο αυτές που σχετίζονται με τα στοιχεία σας:</p>
        <ul>
          <li><strong>Βασικό Φορτίο</strong> — η σταθερή κατανάλωση που πρέπει να καλυφθεί κάθε ώρα.</li>
          <li><strong>Ευέλικτο Φορτίο</strong> — κατανάλωση που η βελτιστοποίηση επιτρέπεται να μετακινήσει στο χρόνο.</li>
          <li><strong>Παραγωγή Φ/Β</strong> (κοινοτική) ή <strong>Φ/Β Οροφής</strong> (ανά κτίριο).</li>
          <li><strong>Κατανομή Κοινοτικής Παραγωγής</strong> — μερίδιο κοινοτικής παραγωγής που κατανέμεται σε αυτό το κτίριο (μόνο στην προβολή ανά κτίριο).</li>
          <li><strong>Παραγωγή Αιολικής</strong> — παραγωγή τυχόν κοινοτικής ανεμογεννήτριας.</li>
          <li><strong>Εισαγωγή από Δίκτυο</strong> — ενέργεια που λαμβάνεται από το δίκτυο εκείνη την ώρα.</li>
          <li><strong>Πλεόνασμα</strong> — παραγωγή που δεν καταναλώθηκε ούτε αποθηκεύτηκε εκείνη την ώρα (εξάγεται ή περικόπτεται).</li>
          <li><strong>Φόρτιση Μπαταρίας</strong> και <strong>Εκφόρτιση Μπαταρίας</strong> — η συνεισφορά της μπαταρίας ανά ώρα.</li>
        </ul>

        <h5 className="mt-3">Μοτίβα που σας λένε κάτι</h5>
        <ul>
          <li><strong>Υψηλή Παραγωγή Φ/Β στη μέση της ημέρας + μπλοκ Πλεονάσματος</strong> — ο ήλιος παρήγαγε περισσότερο από όσο μπόρεσε να χρησιμοποιήσει η κοινότητα· χωρίς μεγαλύτερη μπαταρία, το υπόλοιπο εξάγεται ή περικόπτεται.</li>
          <li><strong>Παραγωγή Φ/Β στο μεσημέρι + Φόρτιση Μπαταρίας την ίδια ώρα</strong> — ο βελτιστοποιητής αποθηκεύει το πλεόνασμα για την βραδινή αιχμή.</li>
          <li><strong>Εκφόρτιση Μπαταρίας το βράδυ + Εισαγωγή από Δίκτυο που παραμένει μικρή</strong> — η μπαταρία κάνει τη δουλειά της· ελάχιστη ακριβή ενέργεια δικτύου χρειάζεται σε ώρες αιχμής τιμών.</li>
          <li><strong>Εισαγωγή από Δίκτυο τη νύχτα + Φόρτιση Μπαταρίας την ίδια ώρα</strong> — έχει νόημα μόνο αν τα νυχτερινά τιμολόγια είναι πολύ φθηνά· αλλιώς ελέγξτε το αρχείο τιμολογίου.</li>
        </ul>
        <Tip>Αντιπαραβάλετε με την <ArticleLink to="results-battery">καμπύλη κατάστασης φόρτισης μπαταρίας</ArticleLink> — αφηγούνται την ίδια ιστορία από δύο γωνίες.</Tip>

        <h5 className="mt-3">Ετήσιες Ενεργειακές Ροές (Μακροπρόθεσμος Σχεδιασμός)</h5>
        <p>
          Ένα διαφορετικό γράφημα με παρόμοιο όνομα εμφανίζεται στα αποτελέσματα του <em>Μακροπρόθεσμου Σχεδιασμού</em> — το
          ραβδόγραμμα <strong>Ετήσιες Ενεργειακές Ροές</strong>. Εκεί, κάθε ράβδος αντιπροσωπεύει ένα έτος της
          προβολής: θετικές τιμές = ενέργεια που καταναλώθηκε ή εισήχθη, αρνητικές τιμές = ενέργεια
          που εξήχθη στο δίκτυο. Χρησιμοποιήστε το για να δείτε πώς μεταβάλλεται το ενεργειακό μίγμα κατά τη διάρκεια ζωής της επένδυσης.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Ανάγνωση των αποτελεσμάτων μπαταρίας',
    summary: 'Τι μας λένε η καμπύλη κατάστασης φόρτισης και οι σειρές φόρτισης/εκφόρτισης της μπαταρίας.',
    body: (
      <>
        <p>
          Όταν μια μπαταρία αποτελεί μέρος της κοινότητάς σας, ο βελτιστοποιητής παράγει μια
          {' '}<strong>Πρόγραμμα Μπαταρίας</strong> προβολή με την <strong>καμπύλη κατάστασης
          φόρτισης (SOC)</strong> που δείχνει πόσο γεμάτη είναι η μπαταρία κάθε ώρα, καθώς και τις
          αντίστοιχες σειρές φόρτισης και εκφόρτισης στο γράφημα <ArticleLink to="results-energy-flow">Ενεργειακό Ισοζύγιο</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="Κατάσταση φόρτισης κατά την βελτιστοποιημένη ημέρα."
        />

        <h5 className="mt-3">Ένα υγιές ημερήσιο μοτίβο</h5>
        <ul>
          <li>Το SOC ανεβαίνει κατά τις ηλιόλουστες ώρες (φόρτιση από Φ/Β) ή κατά τις ώρες χαμηλής τιμής.</li>
          <li>Το SOC πέφτει κατά τη βραδινή αιχμή (εκφόρτιση στα φορτία, αντικαθιστώντας ακριβή εισαγωγή δικτύου).</li>
          <li>Το SOC παραμένει εντός των ρυθμισμένων ορίων — οι προεπιλογές είναι <strong>Ελάχ. 10%</strong> και
            <strong> Μέγ. 90%</strong>, ρυθμισμένα στο στοιχείο μπαταρίας.</li>
          <li>Η καμπύλη δεν πρέπει να παραμένει επίπεδη στο ελάχιστο ή μέγιστο για πολλές ώρες — αυτό
            υποδηλώνει ότι η μπαταρία είναι είτε πολύ μικρή είτε υποχρησιμοποιημένη.</li>
        </ul>

        <h5 className="mt-3">Ρυθμίσεις μπαταρίας για έλεγχο (στο στοιχείο)</h5>
        <p>
          Η συμπεριφορά της μπαταρίας στη βελτιστοποίηση διαμορφώνεται από αυτές τις ρυθμίσεις στη σελίδα
          κοινοτικού ή κτιριακού στοιχείου:
        </p>
        <ul>
          <li><strong>Χωρητικότητα (kWh)</strong> — πόση ενέργεια χωράει εσωτερικά.</li>
          <li><strong>Ρυθμός φόρτισης / εκφόρτισης (kW)</strong> — πόσο γρήγορα μπορεί να μεταφέρει ενέργεια εντός και εκτός.</li>
          <li><strong>Απόδοση πλήρους κύκλου (%)</strong> — ενέργεια εξόδου διαιρεμένη με ενέργεια εισόδου. Η προεπιλογή
            είναι 90%. Ρεαλιστικές πραγματικές τιμές Li-ion είναι 85–95% — οτιδήποτε αισθητά χαμηλότερο σημαίνει ότι
            οι απώλειες μοντελοποιούνται επιθετικά.</li>
          <li><strong>Ελάχ. / Μέγ. SOC (%)</strong> — το λειτουργικό παράθυρο, προεπιλογή 10% έως 90%.</li>
          <li><strong>Αρχικό SOC (%)</strong> — από πού ξεκινά η μπαταρία (προεπιλογή 50%).</li>
        </ul>

        <h5 className="mt-3">Ανάγνωση του προγράμματος παράλληλα με το SOC</h5>
        <p>
          Συγκρίνετε την καμπύλη SOC με τις σειρές Φόρτισης Μπαταρίας / Εκφόρτισης Μπαταρίας στο
          {' '}<ArticleLink to="results-energy-flow">γράφημα Ενεργειακό Ισοζύγιο</ArticleLink>: κάθε ώρα
          «φόρτισης» πρέπει να ανεβάζει το SOC, κάθε ώρα «εκφόρτισης» πρέπει να το κατεβάζει. Ένα άλμα
          στο SOC χωρίς αντίστοιχη φόρτιση στο πρόγραμμα σημαίνει ότι τα δεδομένα επεξεργάστηκαν μεταξύ
          των προβολών και δεν πρέπει να συμβαίνει σε μία μόνο εκτέλεση.
        </p>
        <Warn>
          Μια μπαταρία που παραμένει στο 100% (ή στο Max SOC) για πολλές ώρες δεν κάνει τίποτα — είτε το
          Φ/Β είναι υπερμεγέθυνο σε σχέση με το φορτίο, το τιμολόγιο δεν έχει χρήσιμη διαφορά τιμής, ή
          ο στόχος/οι περιορισμοί εμποδίζουν την εκφόρτιση. Δοκιμάστε τον στόχο <em>Ελαχιστοποίηση κόστους</em> με
          ένα πραγματικό τιμολόγιο Day-Ahead για να δείτε αν η μπαταρία γίνεται πιο ενεργή.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Ανάγνωση των αποτελεσμάτων Μακροπρόθεσμου Σχεδιασμού (CBA)',
    summary: 'NPV, BCR, αποπληρωμή, ετήσιες αποταμιεύσεις, CO₂ — τι σημαίνει κάθε αριθμός και πώς να συγκρίνετε σενάρια.',
    body: (
      <>
        <p>
          Ο <strong>Μακροπρόθεσμος Σχεδιασμός</strong> συγκρίνει ένα ή περισσότερα επενδυτικά σενάρια (νέο Φ/Β,
          νέα μπαταρία, νέοι φορτιστές EV κ.λπ.) με τη <em>βασική γραμμή</em> της αδράνειας,
          σε ένα ορίζοντα που επιλέγετε (συνήθως 5, 10 ή 20 χρόνια). Το αποτέλεσμα είναι ένας πίνακας
          σύγκρισης χρηματοοικονομικών KPI παράλληλα με γραφήματα.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Σύγκριση σεναρίων από τον Μακροπρόθεσμο Σχεδιασμό."
        />

        <h5 className="mt-3">Τι σημαίνει κάθε στήλη</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Στήλη</th><th>Απλή ερμηνεία</th><th>Ευνοϊκό αν…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Συνολικό κέρδος (σε σημερινές αξίες) κατά τη διάρκεια ζωής του έργου, μετά από εφαρμογή του προεξοφλητικού επιτοκίου.</td><td>Θετικό (και όσο το δυνατόν μεγαλύτερο).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Αναλογία Οφελών-Κόστους: συνολικά οφέλη διαιρεμένα με συνολικά κόστη.</td><td>Πάνω από 1,0.</td></tr>
            <tr><td><strong>Αποπληρωμή (έτη)</strong></td><td>Χρόνια έως ότου οι σωρευτικές αποταμιεύσεις καλύψουν το αρχικό κόστος.</td><td>Μικρότερο από τη διάρκεια ζωής του στοιχείου (π.χ. το ηλιακό διαρκεί 20–25 χρόνια).</td></tr>
            <tr><td><strong>Ετήσιες αποταμιεύσεις</strong></td><td>Μέσες αποταμιεύσεις ανά έτος έναντι της βασικής γραμμής (ή ετήσιο κόστος δικτύου για τη γραμμή βάσης).</td><td>Όσο μεγαλύτερο τόσο καλύτερο — αλλά συγκρίνετε με το αρχικό κόστος.</td></tr>
            <tr><td><strong>CO₂ που αποφεύχθηκε / έτος</strong></td><td>Τόνοι CO₂ που δεν εκπέμφθηκαν ανά έτος σε σχέση με τη βασική γραμμή (χρησιμοποιεί τον συντελεστή CO₂ δικτύου της κοινότητάς σας).</td><td>Μεγαλύτερο είναι καλύτερο· χρήσιμο για αναφορές βιωσιμότητας.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Σημείωση για το προεξοφλητικό επιτόκιο</h5>
        <p>
          Η σελίδα εκθέτει ένα πεδίο εισαγωγής <strong>Προεξοφλητικό Επιτόκιο</strong> (προεπιλογή 8%) και έναν πίνακα
          ευαισθησίας. Το NPV υπολογίζεται για το επιτόκιο που ορίζετε. Δοκιμάστε να το μεταβάλετε κατά ±2 ποσοστιαίες
          μονάδες για να δείτε πόσο ευαίσθητο είναι το συμπέρασμά σας — δείτε τον πίνακα <em>ευαισθησίας προεξοφλητικού επιτοκίου</em> στην
          ίδια σελίδα.
        </p>

        <h5 className="mt-3">Πώς να συγκρίνετε σενάρια</h5>
        <ol>
          <li>Κοιτάξτε πρώτα το <strong>NPV</strong> — είναι η ενιαία καλύτερη χρηματοοικονομική σύνοψη. Το σενάριο με το υψηλότερο NPV είναι ο χρηματοοικονομικός νικητής και επισημαίνεται στη σελίδα.</li>
          <li>Επαληθεύστε με το <strong>BCR</strong> και την <strong>αποπληρωμή</strong> — αν το NPV είναι υψηλό αλλά η αποπληρωμή είναι 18 χρόνια σε ένα στοιχείο που διαρκεί 20, η υπόθεση είναι εύθραυστη σε παραδοχές.</li>
          <li>Αν δύο σενάρια ισοβαθμούν στο NPV, προτιμήστε το υψηλότερο BCR ή το χαμηλότερο αρχικό κόστος.</li>
          <li>Συγκρίνετε το <strong>CO₂ που αποφεύχθηκε</strong> ξεχωριστά — το καλύτερο περιβαλλοντικό σενάριο μπορεί να διαφέρει από το καλύτερο χρηματοοικονομικό, και η σελίδα το επισημαίνει ανάλογα.</li>
        </ol>

        <Warn>
          Ένα θετικό NPV εξαρτάται από τις παραδοχές εισαγωγής — πληθωρισμός τιμής ηλεκτρισμού, προεξοφλητικό
          επιτόκιο, διάρκεια ζωής στοιχείου. Χρησιμοποιήστε τον πίνακα ευαισθησίας στη σελίδα για να δείτε αν το NPV
          παραμένει θετικό υπό μια μέτρια αλλαγή στο προεξοφλητικό επιτόκιο· αν αναστρέφεται εύκολα σε αρνητικό, η
          υπόθεση δεν είναι εύρωστη.
        </Warn>
        <Tip>
          Η σελίδα αποτελεσμάτων της πλατφόρμας εμφανίζει τα καλύτερα χρηματοοικονομικά και περιβαλλοντικά σενάρια ως
          συνοπτικές επισημάνσεις. Χρησιμοποιήστε τα για γρήγορη ανάγνωση, αλλά πάντα επιθεωρήστε τον πλήρη πίνακα
          πριν προτείνετε μια επένδυση.
        </Tip>
      </>
    ),
  },
]
